<script setup>
import { ref, shallowRef, watch, computed, nextTick, onBeforeUnmount } from 'vue'
import {
  loadImageDataFromUrl,
  removeWhiteBackground,
  composeStickerOutline,
  cropToContent,
  imageDataToPngBlob,
  blobToDataUrl,
} from '@core/utils/imageBgRemoval.js'
import { removeBackgroundAI, matteToForegroundMask } from '@core/utils/aiBgRemoval.js'
import { replaceCatalogImage } from '@core/services/catalog.js'
import { useToast } from '@core/composables/useToast.js'

const props = defineProps({
  open: {
    type: Boolean,
    default: false,
  },
  // Catalog item: { makat, name, image }
  item: {
    type: Object,
    default: null,
  },
})

const emit = defineEmits(['close', 'replaced'])

const toast = useToast()

// ============================================
// State
// ============================================
// 'loading' | 'load-error' | 'ready' | 'processing' | 'preview' | 'saving'
const phase = ref('loading')
// 'remove' = flush cut ("הסר רקע") | 'outline' = keep a bg stroke ("הקף מוצר")
const mode = ref('remove')
// 'ai' = neural segmentation (BiRefNet) | 'classic' = flood-fill heuristic
const engine = ref('ai')
const aiStatus = ref('')
const tolerance = ref(30)
const removedPct = ref(null)
const previewBg = ref('checker') // 'checker' | 'dark' | 'light'
const showOriginal = ref(false)
const loadError = ref('')

// ImageData objects are huge — keep them out of deep reactivity
const sourceData = shallowRef(null)
const resultData = shallowRef(null)

const resultCanvas = ref(null)

const itemMakat = computed(() => props.item?.makat || props.item?.id || '')
const isBusy = computed(() => phase.value === 'saving' || phase.value === 'processing')

const resultDims = computed(() =>
  resultData.value ? `${resultData.value.width}×${resultData.value.height}` : ''
)

// Show the source <img> until there is a result to draw (incl. first AI run)
const showSourceImage = computed(
  () =>
    phase.value === 'ready' ||
    showOriginal.value ||
    (phase.value === 'processing' && !resultData.value)
)

const resultWarning = computed(() => {
  if (phase.value !== 'preview' || removedPct.value === null) return ''
  if (removedPct.value < 2) {
    return 'כמעט ולא זוהה רקע להסרה — נסו להעלות את הרגישות ולהפעיל את הפעולה שוב'
  }
  if (removedPct.value > 90) {
    return 'הוסר חלק גדול מאוד מהתמונה — בדקו היטב את התוצאה לפני אישור'
  }
  return ''
})

// ============================================
// Open / Load
// ============================================
watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) {
      mode.value = 'remove'
      engine.value = 'ai'
      aiStatus.value = ''
      tolerance.value = 30
      previewBg.value = 'checker'
      showOriginal.value = false
      removedPct.value = null
      resultData.value = null
      loadSource()
    }
  }
)

async function loadSource() {
  if (!props.item?.image) {
    phase.value = 'load-error'
    loadError.value = 'לפריט זה אין תמונה'
    return
  }
  phase.value = 'loading'
  loadError.value = ''
  try {
    sourceData.value = await loadImageDataFromUrl(props.item.image)
    phase.value = 'ready'
  } catch (err) {
    console.error('Failed to load image for bg removal:', err)
    phase.value = 'load-error'
    loadError.value = 'טעינת התמונה נכשלה'
  }
}

// ============================================
// Process / Retry
// ============================================
// "הקף מוצר": base stroke width, scaled with resolution so it stays visually
// ~8px when the (possibly large) image is displayed at product-card size
const OUTLINE_BASE_PX = 8

function outlineWidthFor(imageData) {
  const maxDim = Math.max(imageData.width, imageData.height)
  return Math.max(OUTLINE_BASE_PX, Math.round((maxDim / 1000) * OUTLINE_BASE_PX))
}

async function process(nextMode) {
  if (!sourceData.value || isBusy.value) return
  if (nextMode) mode.value = nextMode

  if (engine.value === 'ai') {
    phase.value = 'processing'
    aiStatus.value = ''
    try {
      const ai = await removeBackgroundAI(sourceData.value, {
        onProgress: () => {
          aiStatus.value = 'מזהה את המוצר... (עשוי לקחת עד דקה)'
        },
      })
      if (!props.open) return
      let composed = ai
      if (mode.value === 'outline') {
        composed = composeStickerOutline(
          sourceData.value,
          matteToForegroundMask(ai.matte),
          outlineWidthFor(sourceData.value)
        )
      }
      finishPreview(composed.imageData, composed.removedRatio)
    } catch (err) {
      console.error('AI background removal failed:', err)
      if (!props.open) return
      toast.warning('מנוע ה-AI לא זמין כרגע — בוצע עיבוד במנוע הקלאסי')
      engine.value = 'classic'
      processClassic()
    }
    return
  }
  processClassic()
}

function processClassic() {
  const result = removeWhiteBackground(sourceData.value, {
    tolerance: tolerance.value,
    outline: mode.value === 'outline' ? outlineWidthFor(sourceData.value) : 0,
  })
  finishPreview(result.imageData, result.removedRatio)
}

function finishPreview(imageData, removedRatio) {
  // Trim empty margins, keep up to 40px padding around the product
  resultData.value = cropToContent(imageData, { padding: 40 })
  removedPct.value = Math.round(removedRatio * 100)
  showOriginal.value = false
  phase.value = 'preview'
  nextTick(drawResult)
}

function drawResult() {
  const canvas = resultCanvas.value
  const data = resultData.value
  if (!canvas || !data) return
  canvas.width = data.width
  canvas.height = data.height
  canvas.getContext('2d').putImageData(data, 0, 0)
}

// ============================================
// Accept / Save
// ============================================
async function accept() {
  if (!resultData.value || isBusy.value) return
  phase.value = 'saving'
  try {
    const blob = await imageDataToPngBlob(resultData.value)
    const dataUrl = await blobToDataUrl(blob)
    const result = await replaceCatalogImage(itemMakat.value, dataUrl)
    toast.success(
      mode.value === 'outline'
        ? 'המוצר הוקף והתמונה הוחלפה בהצלחה'
        : 'הרקע הוסר והתמונה הוחלפה בהצלחה'
    )
    emit('replaced', { makat: itemMakat.value, imageUrl: result.imageUrl })
    emit('close')
  } catch (err) {
    console.error('Failed to save processed image:', err)
    const message = err?.details?.message || err?.message || 'שמירת התמונה נכשלה'
    toast.error(message)
    phase.value = 'preview'
  }
}

// ============================================
// Close
// ============================================
function requestClose() {
  if (isBusy.value) return
  emit('close')
}

function onKeydown(event) {
  if (event.key === 'Escape') requestClose()
}

watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) {
      document.addEventListener('keydown', onKeydown)
    } else {
      document.removeEventListener('keydown', onKeydown)
    }
  },
  { immediate: true }
)

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKeydown)
})
</script>

<template>
  <Teleport to="body">
    <div v-if="open && item" class="bgm-overlay" @click.self="requestClose">
      <div class="bgm-modal" role="dialog" aria-modal="true" aria-label="הסרת רקע מתמונה">
        <!-- Header -->
        <div class="bgm-header">
          <div class="bgm-header-info">
            <h2 class="bgm-title">הסרת רקע מתמונה</h2>
            <span class="bgm-subtitle">{{ item.name }} · {{ itemMakat }}</span>
          </div>
          <button class="bgm-close" :disabled="isBusy" aria-label="סגירה" @click="requestClose">
            <svg
              viewBox="0 0 24 24"
              width="20"
              height="20"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Stage -->
        <div class="bgm-stage">
          <!-- Loading source -->
          <div v-if="phase === 'loading'" class="bgm-state">
            <span class="bgm-spinner"></span>
            <p>טוען תמונה...</p>
          </div>

          <!-- Load error -->
          <div v-else-if="phase === 'load-error'" class="bgm-state">
            <p class="bgm-error-text">{{ loadError }}</p>
            <button class="bgm-btn" @click="loadSource">נסה לטעון שוב</button>
          </div>

          <!-- Original / Result -->
          <template v-else>
            <div
              class="bgm-stage-inner"
              :class="{
                'bg-checker': previewBg === 'checker',
                'bg-dark': previewBg === 'dark',
                'bg-light': previewBg === 'light',
              }"
            >
              <img v-if="showSourceImage" :src="item.image" :alt="item.name" class="bgm-image" />
              <canvas v-show="!showSourceImage" ref="resultCanvas" class="bgm-image"></canvas>
            </div>

            <!-- Preview toolbar -->
            <div v-if="phase === 'preview' || phase === 'saving'" class="bgm-toolbar">
              <div class="bgm-compare">
                <button
                  class="bgm-chip"
                  :class="{ active: showOriginal }"
                  @click="showOriginal = true"
                >
                  לפני
                </button>
                <button
                  class="bgm-chip"
                  :class="{ active: !showOriginal }"
                  @click="showOriginal = false"
                >
                  אחרי
                </button>
              </div>
              <div class="bgm-bg-picker" title="רקע תצוגה מקדימה">
                <button
                  class="bgm-swatch swatch-checker"
                  :class="{ active: previewBg === 'checker' }"
                  aria-label="רקע שקוף"
                  @click="previewBg = 'checker'"
                ></button>
                <button
                  class="bgm-swatch swatch-dark"
                  :class="{ active: previewBg === 'dark' }"
                  aria-label="רקע כהה"
                  @click="previewBg = 'dark'"
                ></button>
                <button
                  class="bgm-swatch swatch-light"
                  :class="{ active: previewBg === 'light' }"
                  aria-label="רקע בהיר"
                  @click="previewBg = 'light'"
                ></button>
              </div>
              <span v-if="removedPct !== null" class="bgm-stats"
                >הוסר {{ removedPct }}% מהתמונה · {{ resultDims }}</span
              >
            </div>
          </template>

          <!-- Busy overlay (AI processing / saving) -->
          <div v-if="phase === 'saving' || phase === 'processing'" class="bgm-saving-overlay">
            <span class="bgm-spinner"></span>
            <p>{{ phase === 'saving' ? 'שומר תמונה...' : aiStatus || 'מעבד...' }}</p>
          </div>
        </div>

        <!-- Warning -->
        <p v-if="resultWarning" class="bgm-warning">{{ resultWarning }}</p>

        <!-- Controls -->
        <div v-if="phase !== 'loading' && phase !== 'load-error'" class="bgm-footer">
          <div class="bgm-slider-group">
            <div class="bgm-engine">
              <span class="bgm-engine-label">מנוע:</span>
              <div class="bgm-compare">
                <button
                  class="bgm-chip"
                  :class="{ active: engine === 'ai' }"
                  :disabled="isBusy"
                  @click="engine = 'ai'"
                >
                  AI
                </button>
                <button
                  class="bgm-chip"
                  :class="{ active: engine === 'classic' }"
                  :disabled="isBusy"
                  @click="engine = 'classic'"
                >
                  קלאסי
                </button>
              </div>
            </div>
            <template v-if="engine === 'classic'">
              <label class="bgm-slider-label" for="bgm-tolerance">
                רגישות
                <span class="bgm-slider-value">{{ tolerance }}</span>
              </label>
              <input
                id="bgm-tolerance"
                v-model.number="tolerance"
                type="range"
                min="0"
                max="100"
                step="5"
                class="bgm-slider"
                :disabled="isBusy"
              />
              <span class="bgm-slider-hint">ערך גבוה מסיר גם צללים וגוונים כמעט-לבנים</span>
            </template>
            <span v-else class="bgm-slider-hint"
              >זיהוי חכם של המוצר בשרת — התוצאה זהה בכל מחשב</span
            >
          </div>

          <div class="bgm-actions">
            <button class="bgm-btn" :disabled="isBusy" @click="requestClose">ביטול</button>
            <template v-if="phase === 'ready'">
              <button class="bgm-btn" @click="process('outline')">
                <svg
                  viewBox="0 0 24 24"
                  width="15"
                  height="15"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <rect x="3" y="3" width="18" height="18" rx="5" stroke-dasharray="4 3" />
                  <circle cx="12" cy="12" r="4" />
                </svg>
                הקף מוצר
              </button>
              <button class="bgm-btn bgm-btn-primary" @click="process('remove')">
                <svg
                  viewBox="0 0 24 24"
                  width="15"
                  height="15"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path
                    d="M15 4V2M15 16v-2M8 9h2M20 9h2M17.8 11.8L19 13M17.8 6.2L19 5M12.2 6.2L11 5M3 21l9-9"
                  />
                </svg>
                הסר רקע
              </button>
            </template>
            <template v-else>
              <button
                class="bgm-btn"
                :class="{ 'bgm-btn-active': mode === 'outline' }"
                :disabled="isBusy"
                @click="process('outline')"
              >
                הקף מוצר
              </button>
              <button
                class="bgm-btn"
                :class="{ 'bgm-btn-active': mode === 'remove' }"
                :disabled="isBusy"
                @click="process('remove')"
              >
                הסר רקע
              </button>
              <button class="bgm-btn bgm-btn-primary" :disabled="isBusy" @click="accept">
                אישור והחלפה
              </button>
            </template>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.bgm-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.72);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: var(--spacing-lg);
}

.bgm-modal {
  background: var(--bg-primary);
  border-radius: var(--border-radius-lg);
  box-shadow: 0 24px 64px rgba(15, 23, 42, 0.4);
  width: min(920px, 96vw);
  max-height: 94vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Header */
.bgm-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-md);
  padding: var(--spacing-md) var(--spacing-lg);
  border-bottom: 1px solid var(--border-color);
}

.bgm-header-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.bgm-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
}

.bgm-subtitle {
  font-size: 0.8rem;
  color: var(--text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.bgm-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: none;
  border-radius: var(--border-radius-sm);
  background: var(--bg-secondary);
  color: var(--text-secondary);
  cursor: pointer;
  transition:
    background 0.15s ease,
    color 0.15s ease;
  flex-shrink: 0;
}

.bgm-close:hover:not(:disabled) {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

/* Stage */
.bgm-stage {
  position: relative;
  flex: 1;
  min-height: 240px;
  display: flex;
  flex-direction: column;
  padding: var(--spacing-md) var(--spacing-lg);
  gap: var(--spacing-sm);
  overflow: hidden;
}

.bgm-stage-inner {
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--border-radius-md, 8px);
  border: 1px solid var(--border-color);
  background: var(--bg-secondary);
  overflow: hidden;
  padding: var(--spacing-sm);
}

.bgm-stage-inner.bg-checker {
  background: repeating-conic-gradient(var(--color-gray-200) 0% 25%, #ffffff 0% 50%) 0 0 / 18px 18px;
}

.bgm-stage-inner.bg-dark {
  background: var(--color-primary-dark);
}

.bgm-stage-inner.bg-light {
  background: #ffffff;
}

.bgm-image {
  max-width: 100%;
  max-height: 56vh;
  width: auto;
  height: auto;
  object-fit: contain;
  display: block;
}

/* Loading / error states */
.bgm-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  color: var(--text-secondary);
}

.bgm-error-text {
  color: var(--color-error);
  font-weight: 600;
  margin: 0;
}

.bgm-spinner {
  width: 28px;
  height: 28px;
  border: 3px solid var(--color-gray-200);
  border-top-color: var(--color-primary);
  border-radius: 50%;
  animation: bgm-spin 0.8s linear infinite;
}

@keyframes bgm-spin {
  to {
    transform: rotate(360deg);
  }
}

/* Saving overlay */
.bgm-saving-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.75);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  color: var(--text-primary);
  font-weight: 600;
  z-index: 2;
}

/* Preview toolbar */
.bgm-toolbar {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  flex-wrap: wrap;
}

.bgm-compare {
  display: flex;
  gap: 4px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  padding: 3px;
}

.bgm-chip {
  border: none;
  background: transparent;
  padding: 4px 12px;
  border-radius: calc(var(--border-radius-sm) - 2px);
  font-size: 0.8rem;
  color: var(--text-secondary);
  cursor: pointer;
  font-family: inherit;
}

.bgm-chip.active {
  background: var(--color-primary);
  color: #ffffff;
}

.bgm-bg-picker {
  display: flex;
  gap: 6px;
  align-items: center;
}

.bgm-swatch {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 2px solid var(--border-color);
  cursor: pointer;
  padding: 0;
}

.bgm-swatch.active {
  border-color: var(--color-primary);
  box-shadow: 0 0 0 2px rgba(30, 41, 59, 0.2);
}

.swatch-checker {
  background: repeating-conic-gradient(var(--color-gray-300) 0% 25%, #ffffff 0% 50%) 0 0 / 8px 8px;
}

.swatch-dark {
  background: var(--color-primary-dark);
}

.swatch-light {
  background: #ffffff;
}

.bgm-stats {
  font-size: 0.8rem;
  color: var(--text-muted);
  margin-inline-start: auto;
}

/* Warning */
.bgm-warning {
  margin: 0;
  padding: var(--spacing-xs) var(--spacing-lg);
  font-size: 0.8rem;
  color: var(--color-warning);
  font-weight: 600;
}

/* Footer */
.bgm-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-lg);
  padding: var(--spacing-md) var(--spacing-lg);
  border-top: 1px solid var(--border-color);
  flex-wrap: wrap;
}

.bgm-slider-group {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 220px;
  flex: 1;
  max-width: 340px;
}

.bgm-engine {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  margin-bottom: 4px;
}

.bgm-engine-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text-secondary);
}

.bgm-slider-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text-secondary);
  display: flex;
  gap: 6px;
  align-items: baseline;
}

.bgm-slider-value {
  color: var(--text-primary);
  font-weight: 700;
}

.bgm-slider {
  width: 100%;
  accent-color: var(--color-primary);
  cursor: pointer;
}

.bgm-slider-hint {
  font-size: 0.7rem;
  color: var(--text-muted);
}

.bgm-actions {
  display: flex;
  gap: var(--spacing-sm);
  align-items: center;
  flex-wrap: wrap;
}

.bgm-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: var(--border-radius-sm);
  border: 1px solid var(--border-color);
  background: var(--bg-primary);
  color: var(--text-primary);
  font-size: 0.85rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition:
    background 0.15s ease,
    border-color 0.15s ease,
    transform 0.1s ease;
}

.bgm-btn:hover:not(:disabled) {
  background: var(--bg-secondary);
}

.bgm-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

/* The mode used for the current preview ("retry" re-runs the highlighted one) */
.bgm-btn-active {
  border-color: var(--color-primary);
  background: var(--bg-secondary);
  box-shadow: inset 0 0 0 1px var(--color-primary);
}

.bgm-btn-primary {
  background: var(--gradient-primary);
  border-color: transparent;
  color: #ffffff;
}

.bgm-btn-primary:hover:not(:disabled) {
  background: var(--color-primary-hover);
  transform: translateY(-1px);
}

/* Mobile */
@media (max-width: 640px) {
  .bgm-overlay {
    padding: var(--spacing-sm);
  }

  .bgm-modal {
    width: 100%;
    max-height: 96vh;
  }

  .bgm-stage {
    padding: var(--spacing-sm);
  }

  .bgm-image {
    max-height: 42vh;
  }

  .bgm-footer {
    flex-direction: column;
    align-items: stretch;
  }

  .bgm-slider-group {
    max-width: none;
  }

  .bgm-actions {
    justify-content: flex-end;
  }
}
</style>
